import { Components, Theme } from "@mui/material/styles";
import type { CSSObject } from "@mui/material/styles";

import { textColors, palette } from "./palette";
import { typography } from "./typography";

const sharedStyles: CSSObject = {
  textTransform: "none",
  borderRadius: 42,
  display: "inline-flex",
  height: "auto",
  flexShrink: 0,
};

const buttonLStyles = {
  ...sharedStyles,
  padding: "18px 36px",
  "&.MuiButton-root": {
    ...typography.bodyMSemiBold,
  },
  "& .MuiButton-startIcon": {
    marginRight: "0.875rem",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "0.875rem",
  },
};

const buttonMStyles = {
  ...sharedStyles,
  height: "3.25rem",
  padding: "14px 32px",
  "&.MuiButton-root": {
    ...typography.bodyMSemiBold,
  },
  "& .MuiButton-startIcon": {
    marginRight: "0.75rem",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "0.75rem",
  },
};

const buttonSStyles = {
  ...sharedStyles,
  height: "2.75rem",
  padding: "10px 24px",
  "&.MuiButton-root": {
    ...typography.bodySSemiBold,
  },
  "& .MuiButton-startIcon": {
    marginRight: "0.625rem",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "0.625rem",
  },
};

const buttonXSStyles = {
  ...sharedStyles,
  height: "2.75rem",
  padding: "6px 24px",
  "&.MuiButton-root": {
    ...typography.bodySSemiBold,
  },
  "& .MuiButton-startIcon": {
    marginRight: "0.5rem",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "0.5rem",
  },
};

const neutralMedium = palette.neutral1?.medium ?? "#AFAFAF";
const white = palette.common?.white ?? "#fff";
const brandDark = palette.brand?.dark ?? "#003C94";

export const MuiButton: Components<Theme>["MuiButton"] = {
  defaultProps: {
    disableElevation: true,
    disableRipple: false,
    size: "medium",
  },
  styleOverrides: {
    root: {
      transition: "background-color 0.2s, color 0.2s, border-color 0.2s",
      ...sharedStyles,
    },
    sizeLarge: {
      ...buttonMStyles,
      "@media (min-width:900px)": buttonLStyles,
    },
    sizeMedium: {
      ...buttonSStyles,
      "@media (min-width:900px)": buttonMStyles,
    },
    sizeSmall: {
      ...buttonXSStyles,
      "@media (min-width:900px)": buttonSStyles,
    },
  },
  variants: [
    {
      props: { variant: "primary" },
      style: {
        backgroundColor: textColors.brand,
        "&.MuiButton-root": {
          color: textColors.contrastText,
        },
        "&:hover": { backgroundColor: brandDark },
      },
    },
    {
      props: { variant: "secondary" },
      style: {
        border: `1px solid ${neutralMedium}`,
        background: white,
        "&.MuiButton-root": {
          color: textColors.secondary,
        },
        "&:hover": { backgroundColor: neutralMedium },
      },
    },
    {
      props: { variant: "tertiary" },
      style: {
        backgroundColor: "transparent",
        border: "none",
        "&.MuiButton-root": {
          color: textColors.secondary,
          paddingLeft: 0,
          paddingRight: 0,
        },
        "&:hover": { textDecoration: "underline" },
      },
    },
    {
      props: { variant: "primaryHC" },
      style: {
        backgroundColor: white,
        border: "none",
        "&.MuiButton-root": {
          color: textColors.secondary,
        },
        "&:hover": { backgroundColor: neutralMedium, textDecoration: "none" },
      },
    },
    {
      props: { variant: "secondaryHC" },
      style: {
        backgroundColor: "rgba(255, 255, 255, 0.10)",
        border: "1px solid var(--mui-palette-common-white, #fff)",
        "&.MuiButton-root": {
          color: textColors.contrastText,
        },
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.20)" },
      },
    },
    {
      props: { variant: "tertiaryHC" },
      style: {
        backgroundColor: "transparent",
        border: "none",
        "&.MuiButton-root": {
          color: textColors.contrastText,
          paddingLeft: 0,
          paddingRight: 0,
        },
        "&:hover": { textDecoration: "underline" },
      },
    },
  ],
};

const iconButtonLStyles = {
  padding: 20,
};

const iconButtonMStyles = {
  padding: 16,
};

const iconButtonSStyles = {
  padding: 12,
};

const iconButtonXSStyles = {
  padding: 8,
};

export const MuiIconButton: Components<Theme>["MuiIconButton"] = {
  defaultProps: {
    color: "default",
    disableRipple: false,
    size: "medium",
  },
  styleOverrides: {
    root: {
      transition: "background-color 0.2s, color 0.2s, border-color 0.2s",
    },
    sizeLarge: {
      ...iconButtonMStyles,
      "@media (min-width:900px)": iconButtonLStyles,
    },
    sizeMedium: {
      ...iconButtonSStyles,
      "@media (min-width:900px)": iconButtonMStyles,
    },
    sizeSmall: {
      ...iconButtonXSStyles,
      "@media (min-width:900px)": iconButtonSStyles,
    },
  },
  variants: [
    {
      props: { color: "default" },
      style: {
        color: textColors.contrastText,
        "&:hover": { backgroundColor: "transparent" },
      },
    },
    {
      props: { color: "primary" },
      style: {
        backgroundColor: "#fff",
        color: textColors.secondary,
        border: `1px solid ${neutralMedium}`,
        "&:hover": { backgroundColor: neutralMedium },
      },
    },
    {
      props: { color: "primaryHC" },
      style: {
        backgroundColor: textColors.contrastText,
        color: textColors.secondary,
        "&:hover": { backgroundColor: "#f5f5f5" },
      },
    },
  ],
};
